import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { requestPasswordReset } from '@/api/auth/authApi';
import { BrandLogo } from '@/components/common/BrandLogo';
import { Icon } from '@/components/common/Icon';
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';

export function ForgotPasswordScreen() {
  const { t } = useTranslation('auth');
  const [emailAddress, setEmailAddress] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [wasSubmitted, setWasSubmitted] = useState(false);

  async function submitRequest(formEvent: FormEvent<HTMLFormElement>) {
    formEvent.preventDefault();
    setIsSubmitting(true);
    try {
      await requestPasswordReset(emailAddress.trim());
    } catch (requestError) {
      void requestError;
    } finally {
      setIsSubmitting(false);
      setWasSubmitted(true);
    }
  }

  return (
    <div className="min-h-screen bg-zoooffwhite flex items-center justify-center p-4">
      <div className="fixed top-4 right-4 z-50">
        <LanguageSwitcher />
      </div>
      <div className="w-full max-w-md bg-zoowhite border border-zooborder rounded-[2.5rem] shadow-xl overflow-hidden">
        <div className="p-8 md:p-10">
          <div className="flex justify-center mb-6">
            <BrandLogo className="h-10 w-auto object-contain" />
          </div>
          <div className="flex justify-center mb-5">
            <div className="w-14 h-14 rounded-2xl bg-zoovpalelavender flex items-center justify-center">
              <Icon name="KeyRound" className="text-xl text-zooprimary" />
            </div>
          </div>
          <div className="text-center mb-7">
            <h1 className="text-2xl font-bold text-zooindigo">{t('forgotPasswordScreen.title')}</h1>
            <p className="text-sm text-zoomuted mt-2 leading-relaxed">
              {t('forgotPasswordScreen.subtitle')}
            </p>
          </div>

          {wasSubmitted === true ? (
            <div
              role="alert"
              className="flex items-start gap-2.5 px-4 py-3 bg-zoosuccessbg border border-zoosuccessborder rounded-xl"
            >
              <Icon name="CheckCircle" className="text-zoosuccesstext mt-0.5" />
              <p className="text-xs text-zoosuccesstext font-medium leading-relaxed">
                {t('forgotPasswordScreen.confirmation')}
              </p>
            </div>
          ) : (
            <form onSubmit={submitRequest} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="forgot-email">{t('forgotPasswordScreen.emailLabel')}</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  required
                  value={emailAddress}
                  placeholder={t('emailPlaceholder')}
                  onChange={function updateEmail(changeEvent) {
                    setEmailAddress(changeEvent.target.value);
                  }}
                />
              </div>
              <Button type="submit" size="large" isLoading={isSubmitting} className="w-full">
                {t('forgotPasswordScreen.submit')}
              </Button>
            </form>
          )}

          <div className="mt-7 text-center">
            <Button asChild variant="link" className="text-sm text-zoomuted hover:text-zooprimary">
              <Link to={APPLICATION_ROUTES.login}>{t('forgotPasswordScreen.backToLogin')}</Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
